class DataCharts {
  constructor(el) {
    this.root = document.querySelector('.' + el);
    if (!this.root) return;

    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    this.duration = 1400;
    this.nums = Array.from(this.root.querySelectorAll('.p-data__num'));
    this.donuts = Array.from(
      this.root.querySelectorAll('.p-data__donut[data-chart]')
    );

    this.prepareNumbers();
    this.observe();
  }

  prepareNumbers() {
    this.nums.forEach((num) => {
      const raw = num.textContent.trim();
      const grouped = raw.includes(',');
      const normalized = raw.replace(/,/g, '');
      const decimals = (normalized.split('.')[1] || '').length;
      const target = Number(normalized);

      if (!Number.isFinite(target)) return;

      num.dataset.countTarget = String(target);
      num.dataset.countDecimals = String(decimals);
      num.dataset.countGrouped = grouped ? '1' : '0';

      if (!this.reduceMotion) {
        this.writeNumber(num, this.formatNumber(0, decimals, grouped));
      }
    });
  }

  observe() {
    if (this.reduceMotion) {
      this.donuts.forEach((donut) => this.drawChart(donut));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.35) return;

          observer.unobserve(entry.target);
          this.play(entry.target);
        });
      },
      { threshold: [0, 0.35, 1] }
    );

    this.donuts.forEach((donut) => observer.observe(donut));
    this.nums.forEach((num) => {
      if (num.closest('.p-data__donut')) return;
      observer.observe(num);
    });
  }

  play(target) {
    if (target.classList.contains('p-data__donut')) {
      target.querySelectorAll('.p-data__num').forEach((num) => this.countUp(num));
      this.drawChart(target);
      return;
    }

    this.countUp(target);
  }

  countUp(num) {
    if (num.dataset.countPlayed === '1') return;
    if (!num.dataset.countTarget) return;

    num.dataset.countPlayed = '1';

    const target = Number(num.dataset.countTarget);
    const decimals = Number(num.dataset.countDecimals) || 0;
    const grouped = num.dataset.countGrouped === '1';
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / this.duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.writeNumber(num, this.formatNumber(target * eased, decimals, grouped));

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }

  kernNumber(formatted) {
    return String(formatted).replace(/1|7|,/g, (char) => {
      if (char === '1') {
        return '<span class="p-data__digit p-data__digit--1">1</span>';
      }

      if (char === '7') {
        return '<span class="p-data__digit p-data__digit--7">7</span>';
      }

      return '<span class="p-data__digit p-data__digit--comma">,</span>';
    });
  }

  writeNumber(num, formatted) {
    const intEl = num.querySelector('.p-data__num-int');
    const decEl = num.querySelector('.p-data__num-dec');

    if (!intEl) {
      num.innerHTML = this.kernNumber(formatted);
      return;
    }

    const dot = formatted.indexOf('.');
    if (dot === -1) {
      intEl.innerHTML = this.kernNumber(formatted);
      if (decEl) decEl.textContent = '';
      return;
    }

    intEl.innerHTML = this.kernNumber(formatted.slice(0, dot + 1));
    if (decEl) decEl.innerHTML = this.kernNumber(formatted.slice(dot + 1));
  }

  formatNumber(value, decimals, grouped) {
    const fixed = value.toFixed(decimals);
    if (!grouped) return fixed;

    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  drawChart(donut) {
    if (donut.dataset.chartPlayed === '1') return;
    if (typeof Chart === 'undefined') return;

    const canvas = donut.querySelector('.p-data__donut-canvas');
    if (!canvas) return;

    const segments = donut.dataset.chart.split(',').map((part) => {
      const [value, color] = part.split(':');
      return {
        value: Number(value),
        color: color,
      };
    }).filter((item) => Number.isFinite(item.value) && item.color);

    if (!segments.length) return;

    const wrap = canvas.parentElement;
    if (!wrap || !wrap.clientWidth || !wrap.clientHeight) {
      requestAnimationFrame(() => this.drawChart(donut));
      return;
    }

    donut.dataset.chartPlayed = '1';
    donut.classList.add('is-charted');

    const values = segments.map((item) => item.value);
    const duration = this.reduceMotion ? 0 : this.duration;
    const cutout = donut.dataset.cutout ? `${donut.dataset.cutout}%` : '72%';
    const chart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: values.map(() => 0),
            backgroundColor: segments.map((item) => item.color),
            borderWidth: 0,
            hoverBorderWidth: 0,
            spacing: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: cutout,
        radius: '100%',
        animation: {
          animateRotate: true,
          animateScale: false,
          duration: 0,
          easing: 'easeOutCubic',
        },
        transitions: {
          resize: {
            animation: {
              duration: 0,
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        events: [],
      },
    });

    // 初期化直後の resize が回転アニメーションを打ち切るため、
    // レイアウト確定後に 0 から実データへ更新して描く。
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        chart.data.datasets[0].data = values;
        chart.config.options.animation.duration = duration;
        chart.config.options.animation.animateRotate = true;
        chart.update();
      });
    });
  }
}
